import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import useRootSelector from 'common/hooks/useRootSelector';

import { List as ImmutableList } from 'immutable';
import { Box, Button, Divider, makeStyles, Typography } from '@material-ui/core';

import { CheckInBlock } from 'common/components/PersonProfile/components';
import { IProfileInfoImt, IReferralCreateDto } from 'common/components/PersonProfile/interfaces';
import messages from 'common/components/PersonProfile/messages';
import * as actions from 'common/components/PersonProfile/state/referrals/actions';
import * as selectors from 'common/components/PersonProfile/state/referrals/selectors';
import { ActionResult } from 'common/constants';
import { PeakModules } from 'common/constants/peakModules';
import { useAppDispatch } from 'store/hooks';
import ReferralSectionActions from './ReferralSectionActions/ReferralSectionActions';
import ReferralCreateModal from './ReferralCreateModal/ReferralCreateModal.';
import { selectReferralData } from '../../state/referrals/selectors';
import { usePersonSelectorTemplate } from '../../hooks/usePersonSelector';
import { IReferralListInfoImt } from 'common/interfaces/referrals';
import ReferralListItem from './ReferralListItem/ReferralListItem';
import PotentialReferralsModal from './PotentialReferralsModal/PotentialReferralsModal';
import AvatarWithInitials from 'common/components/AvatarWithInitials/AvatarWithInitials';
import { getInitials } from 'helpers/common';
import { searchNoteCodesThunk } from '../../state/actions';
import { INoteCodeDictionaryItemImt } from 'common/interfaces/dictionary';
import { selectNoteCodes } from '../../state/selectors';
import { selectCurrentUserAvailableClubs } from 'modules/authentication/state/selectors';
import { getRefferalsClubInfo } from '../../utils';
import useSearchReferralMembers from 'common/hooks/useSearchReferralMembers';

interface IReferralSectionProps {
  module: PeakModules;
  profile: IProfileInfoImt;
}

const useStyles = makeStyles(() => ({
  referralTitle: {
    fontWeight: 700,
  },

  referralName: {
    display: 'flex',
    flexDirection: 'column',

    '& span': {
      lineHeight: '1.125rem',
      fontWeight: 700,
    },
  },
}));

const ReferralSection = (props: IReferralSectionProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const { module, profile } = props;
  const profileId = Number(profile.get('id'));

  const usePersonSelector = usePersonSelectorTemplate(profileId);

  const referrals: ImmutableList<IReferralListInfoImt> = usePersonSelector(selectReferralData);
  const referredBy = usePersonSelector(selectors.selectReferredBy)?.toJS();
  const addReferralResult = useRootSelector(
    selectors.selectAddPersonReferralActionResult(profileId),
  );
  const addReferralLoading = useRootSelector(selectors.selectAddPersonReferralLoading(profileId));
  const setReferralResult = useRootSelector(selectors.selectSetReferredByActionResult(profileId));
  const noteCodes: ImmutableList<INoteCodeDictionaryItemImt> = useRootSelector(
    selectNoteCodes(profileId),
  );
  const clubs = useRootSelector(selectCurrentUserAvailableClubs);

  const [referralsViewModal, setReferralsViewModal] = useState(false);
  const [referralCreateModalOpen, setReferralCreateModalOpen] = useState(false);

  const classes = useStyles();

  useEffect(() => {
    dispatch(actions.fetchPersonReferrals(profileId));
  }, [dispatch, profileId]);

  useEffect(() => {
    if (setReferralResult === ActionResult.SUCCESS_ACTION) {
      dispatch(actions.setReferralAction(null, profileId));
      setReferralsViewModal(false);
      dispatch(actions.fetchPersonReferrals(profileId));
    }
  }, [setReferralResult, dispatch, profileId, setReferralsViewModal]);

  useEffect(() => {
    if (addReferralResult === ActionResult.SUCCESS_ACTION) {
      dispatch(actions.addPersonReferralAction(null, profileId));
      setReferralCreateModalOpen(false);
      dispatch(actions.fetchPersonReferrals(profileId));
    }
  }, [addReferralResult, dispatch, profileId, setReferralCreateModalOpen]);

  useEffect(() => {
    return () => {
      dispatch(actions.resetPersonReferrals(null, profileId));
      dispatch(actions.setPersonReferralsErrorAction(null, profileId));
    };
  }, [dispatch, profileId]);

  const getReferralMembers = useSearchReferralMembers({ module, memberId: profileId });

  const onAddNewClick = () => {
    setReferralCreateModalOpen(true);
  };

  const onSendReferral = (value: string) => {
    dispatch(actions.setReferral(profileId, { referredBy: value }));
  };

  const onCreateReferral = (dto: IReferralCreateDto) => {
    dispatch(actions.addPersonReferral(profileId, dto));
  };

  const searchNoteCodes = (search: string) => {
    dispatch(searchNoteCodesThunk(search, profileId));
  };

  return (
    <CheckInBlock
      title={<FormattedMessage {...messages.referralsBlockTitle} />}
      buttonGroup={<ReferralSectionActions onAddNewClick={onAddNewClick} />}
    >
      <Box>
        <Box>
          <Typography variant="caption" color="textSecondary" className={classes.referralTitle}>
            <FormattedMessage {...messages.referralCameFrom} />
          </Typography>
        </Box>
        {referredBy ? (
          <Box display="flex" alignItems="center" py={1}>
            <Box marginRight={1} display="flex">
              <AvatarWithInitials
                initials={getInitials(
                  referredBy.title.split(/\s(.*)/)[0],
                  referredBy.title.split(/\s(.*)/)[1],
                )}
                imageUrl={referredBy.imageUrl}
                width="35px"
                height="35px"
                marginRight={0}
              />
            </Box>

            <Typography className={classes.referralName}>
              <Typography component="span" className={classes.referralTitle}>
                {referredBy.title}
              </Typography>
              <Typography color="textSecondary">
                {getRefferalsClubInfo(referredBy.club?.title, referredBy.club?.address)}
              </Typography>
            </Typography>
          </Box>
        ) : (
          <Box mt={1} mb={2}>
            <Button onClick={() => setReferralsViewModal(true)} color="primary" fullWidth>
              <FormattedMessage {...messages.selectReferral} />
            </Button>
          </Box>
        )}

        <Divider />

        <Box mt={1}>
          <Typography variant="caption" color="textSecondary" className={classes.referralTitle}>
            <FormattedMessage {...messages.sentStatus} />
          </Typography>
        </Box>

        {referrals?.size && referrals.filter(item => !item.get('accepted')).size ? (
          <Box>
            {referrals
              .filter(item => !item.get('accepted'))
              .map(item => {
                return (
                  <React.Fragment key={item.get('id')}>
                    <ReferralListItem item={item} />
                  </React.Fragment>
                );
              })}
          </Box>
        ) : (
          <Box className="empty-section-placeholder" p={2}>
            <Typography className="empty-text">
              <FormattedMessage {...messages.emptyReferralsListMessage} />
            </Typography>
          </Box>
        )}
      </Box>

      <Divider />

      <Box>
        <Box mt={1}>
          <Typography variant="caption" color="textSecondary" className={classes.referralTitle}>
            <FormattedMessage {...messages.acceptedStatus} />
          </Typography>
        </Box>

        {referrals?.size && referrals.filter(item => item.get('accepted')).size ? (
          <Box>
            {referrals
              .filter(item => item.get('accepted'))
              .map(item => {
                return (
                  <React.Fragment key={item.get('id')}>
                    <ReferralListItem item={item} />
                  </React.Fragment>
                );
              })}
          </Box>
        ) : (
          <Box className="empty-section-placeholder" p={2}>
            <Typography className="empty-text">
              <FormattedMessage {...messages.emptyReferralsListMessage} />
            </Typography>
          </Box>
        )}
      </Box>

      {referralsViewModal && (
        <PotentialReferralsModal
          isOpen
          onSendReferral={onSendReferral}
          onClose={() => {
            setReferralsViewModal(false);
          }}
          getReferralByValue={getReferralMembers}
          personId={profileId}
        />
      )}

      {referralCreateModalOpen && (
        <ReferralCreateModal
          onCreateReferral={onCreateReferral}
          isLoading={addReferralLoading}
          isOpen
          onClose={() => {
            setReferralCreateModalOpen(false);
          }}
          profileId={profileId}
          noteCodeList={noteCodes?.toJS()}
          searchNoteCodes={searchNoteCodes}
          clubs={clubs}
        />
      )}
    </CheckInBlock>
  );
};

export default ReferralSection;
